import { db, auth } from '../firebase';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs,
  updateDoc, 
  deleteDoc,
  query, 
  where,
  serverTimestamp,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';

const CLASSES_COLLECTION = 'classes';
const CLASS_INVITATIONS_COLLECTION = 'classInvitations';
const USERS_COLLECTION = 'users';

// Import classEventService - need to import after definition to avoid circular dependency
let classEventService;

export const classService = {
  async createClass(className) {
    const user = auth.currentUser;
    if (!user) throw new Error('User must be authenticated');

    const classId = `${className.replace(/\s+/g, '')}_${Date.now()}`;
    
    const classData = {
      id: classId,
      name: className,
      adminId: user.uid,
      adminEmail: user.email,
      createdAt: serverTimestamp(),
      members: [user.uid],
      memberCount: 1
    };

    await setDoc(doc(db, CLASSES_COLLECTION, classId), classData);
    
    await updateDoc(doc(db, USERS_COLLECTION, user.uid), {
      classID: classId,
      classRole: 'admin',
      classSelection: className
    });

    return classData;
  },

  async sendClassInvitation(classId, recipientEmail) {
    const user = auth.currentUser;
    if (!user) throw new Error('User must be authenticated');

    const classDoc = await getDoc(doc(db, CLASSES_COLLECTION, classId));
    if (!classDoc.exists()) throw new Error('Class not found');
    
    const classData = classDoc.data();
    if (classData.adminId !== user.uid) {
      throw new Error('Only class admin can send invitations');
    }

    const invitationId = `${classId}_${recipientEmail.replace('@', '_at_')}_${Date.now()}`;
    
    const invitationData = {
      id: invitationId,
      classId: classId,
      className: classData.name,
      inviterEmail: user.email,
      inviterName: user.displayName || user.email,
      recipientEmail: recipientEmail,
      status: 'pending',
      createdAt: serverTimestamp()
    };

    await setDoc(doc(db, CLASS_INVITATIONS_COLLECTION, invitationId), invitationData);
    
    const invitationLink = `${window.location.origin}/class-invite/${invitationId}`;
    
    return { ...invitationData, invitationLink };
  },

  async getClassInvitations(email) {
    const q = query(
      collection(db, CLASS_INVITATIONS_COLLECTION),
      where('recipientEmail', '==', email),
      where('status', '==', 'pending')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async getPendingInvitationsForClass(classId) {
    const q = query(
      collection(db, CLASS_INVITATIONS_COLLECTION),
      where('classId', '==', classId),
      where('status', '==', 'pending')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async acceptClassInvitation(invitationId) {
    const user = auth.currentUser;
    if (!user) throw new Error('User must be authenticated');

    const invitationDoc = await getDoc(doc(db, CLASS_INVITATIONS_COLLECTION, invitationId));
    if (!invitationDoc.exists()) throw new Error('Invitation not found');
    
    const invitation = invitationDoc.data();
    
    if (invitation.recipientEmail !== user.email) {
      throw new Error('This invitation is not for you');
    }

    await updateDoc(doc(db, CLASS_INVITATIONS_COLLECTION, invitationId), {
      status: 'accepted',
      acceptedAt: serverTimestamp()
    });

    const classDoc = await getDoc(doc(db, CLASSES_COLLECTION, invitation.classId));
    const currentCount = classDoc.data()?.memberCount || 1;
    
    await updateDoc(doc(db, CLASSES_COLLECTION, invitation.classId), {
      members: arrayUnion(user.uid),
      memberCount: currentCount + 1
    });

    await updateDoc(doc(db, USERS_COLLECTION, user.uid), {
      classID: invitation.classId,
      classRole: 'member',
      classSelection: invitation.className
    });

    // Distribute existing class events to the new member
    try {
      // Lazy load classEventService to avoid circular dependency
      if (!classEventService) {
        classEventService = (await import('./classEventService')).classEventService;
      }
      
      const distributionResult = await classEventService.distributeExistingEventsToNewMember(
        user.uid, 
        invitation.classId
      );
      
      console.log(`✅ New member ${user.email} received ${distributionResult.addedCount} existing class events`);
      
      return {
        ...invitation,
        eventsDistributed: distributionResult.addedCount,
        totalExistingEvents: distributionResult.totalEvents
      };
    } catch (eventError) {
      console.error('Error distributing existing events to new member:', eventError);
      // Don't fail the invitation acceptance if event distribution fails
      return {
        ...invitation,
        eventsDistributed: 0,
        distributionError: eventError.message
      };
    }
  },

  async declineClassInvitation(invitationId) {
    const user = auth.currentUser;
    if (!user) throw new Error('User must be authenticated');

    const invitationDoc = await getDoc(doc(db, CLASS_INVITATIONS_COLLECTION, invitationId));
    if (!invitationDoc.exists()) throw new Error('Invitation not found');
    
    const invitation = invitationDoc.data();
    
    if (invitation.recipientEmail !== user.email) {
      throw new Error('This invitation is not for you');
    }

    await updateDoc(doc(db, CLASS_INVITATIONS_COLLECTION, invitationId), {
      status: 'declined',
      declinedAt: serverTimestamp()
    });
  },

  async getClassInfo(classId) {
    const classDoc = await getDoc(doc(db, CLASSES_COLLECTION, classId));
    if (!classDoc.exists()) return null;
    
    return { id: classDoc.id, ...classDoc.data() };
  },

  async getClassMembers(classId) {
    const q = query(
      collection(db, USERS_COLLECTION),
      where('classID', '==', classId)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data(),
      isAdmin: doc.data().classRole === 'admin'
    }));
  },

  async isClassAdmin(userId, classId) {
    const userDoc = await getDoc(doc(db, USERS_COLLECTION, userId));
    if (!userDoc.exists()) return false;
    
    const userData = userDoc.data();
    return userData.classID === classId && userData.classRole === 'admin';
  },

  async leaveClass(classId) {
    const user = auth.currentUser;
    if (!user) throw new Error('User must be authenticated');

    const userDoc = await getDoc(doc(db, USERS_COLLECTION, user.uid));
    if (!userDoc.exists()) throw new Error('User not found');
    
    const userData = userDoc.data();
    
    if (userData.classRole === 'admin') {
      throw new Error('Admin cannot leave the class. Please transfer admin rights first.');
    }

    const classDoc = await getDoc(doc(db, CLASSES_COLLECTION, classId));
    const currentCount = classDoc.data()?.memberCount || 2;
    
    await updateDoc(doc(db, CLASSES_COLLECTION, classId), {
      members: arrayRemove(user.uid),
      memberCount: Math.max(0, currentCount - 1)
    });

    await updateDoc(doc(db, USERS_COLLECTION, user.uid), {
      classID: null,
      classRole: null,
      classSelection: null
    });
  },

  async getUserClasses(userId) {
    const userDoc = await getDoc(doc(db, USERS_COLLECTION, userId));
    if (!userDoc.exists()) return [];
    
    const userData = userDoc.data();
    if (!userData.classID) return [];
    
    const classInfo = await this.getClassInfo(userData.classID);
    return classInfo ? [classInfo] : [];
  }
};